import { db, kvGet, kvSet } from './db'
import { freePlates } from './lib/plates'
import { getManifest, getSeries, getActuals, getLicence, type Manifest, type ManifestStation, type ManifestShallow, type ManifestSublocation, type ManifestBridge, type ActualsSnapshot, type Licence } from './api'
import { noteServerVersion } from './update'

/**
 * Synchronisation: compare the server manifest with what is stored and download only the
 * series whose hash changed. All 71 stations for a year are about 8 MB, so everything is
 * kept locally and the app works offline for as long as the predictions run.
 */
export interface SyncProgress {
  state: 'idle' | 'running' | 'done' | 'failed' | 'offline'
  done: number
  total: number
  message?: string
}

const CONCURRENCY = 3

export async function loadStations(): Promise<ManifestStation[]> {
  return (await kvGet<ManifestStation[]>('stations')) ?? []
}

export async function loadShallows(): Promise<ManifestShallow[]> {
  return (await kvGet<ManifestShallow[]>('shallows')) ?? []
}

export async function loadSublocations(): Promise<ManifestSublocation[]> {
  return (await kvGet<ManifestSublocation[]>('sublocations')) ?? []
}

export async function loadBridges(): Promise<ManifestBridge[]> {
  return (await kvGet<ManifestBridge[]>('bridges')) ?? []
}

export async function loadActuals(): Promise<ActualsSnapshot | null> {
  return (await kvGet<ActualsSnapshot>('actuals')) ?? null
}

/**
 * The licence as the server last stated it. It is kept because the app starts offline as
 * often as not, and the day strip should still know how far this device may read.
 */
export async function loadLicence(): Promise<Licence | null> {
  return (await kvGet<Licence>('licence')) ?? null
}

export async function storeLicence(licence: Licence | null): Promise<Licence | null> {
  if (licence) await kvSet('licence', licence)
  return licence
}

/** Ask the server what this device may read, keeping the stored answer when it cannot be reached */
export async function syncLicence(): Promise<Licence | null> {
  if (!navigator.onLine) return loadLicence()
  try {
    return await storeLicence(await getLicence())
  } catch {
    return loadLicence()
  }
}

/**
 * Fetch the measured and forecast levels. The stored copy's ETag goes along, so the server
 * answers 304 with no body while nothing has changed, and the stored copy stays when the
 * network fails.
 */
export async function syncActuals(): Promise<ActualsSnapshot | null> {
  const stored = await loadActuals()
  if (!navigator.onLine) return stored
  try {
    const result = await getActuals(stored ? (await kvGet<string>('actualsEtag')) ?? null : null)
    if (!result) return stored
    await kvSet('actuals', result.snapshot)
    await kvSet('actualsEtag', result.etag)
    return result.snapshot
  } catch {
    return stored
  }
}

export async function serverVersion(): Promise<string | null> {
  return (await kvGet<string | null>('serverVersion')) ?? null
}

export async function lastSyncTime(): Promise<string | null> {
  return (await kvGet<string>('lastSync')) ?? null
}

export async function sync(onProgress: (p: SyncProgress) => void): Promise<void> {
  if (!navigator.onLine) {
    onProgress({ state: 'offline', done: 0, total: 0 })
    return
  }
  let manifest: Manifest
  try {
    manifest = await getManifest()
  } catch (e) {
    onProgress({ state: 'failed', done: 0, total: 0, message: e instanceof Error ? e.message : String(e) })
    return
  }
  await kvSet('stations', manifest.stations)
  await kvSet('shallows', manifest.shallows ?? [])
  await kvSet('sublocations', manifest.sublocations ?? [])
  await kvSet('bridges', manifest.bridges ?? [])
  await kvSet('serverVersion', manifest.version ?? null)
  if (manifest.licence) await kvSet('licence', manifest.licence)
  noteServerVersion(manifest.version)

  const stored = new Map((await db.series.toArray()).map(r => [r.key, { hash: r.hash, size: r.bytes.byteLength }]))
  const wanted = manifest.series.map(s => ({ ...s, key: `${s.station}:${s.year}:${s.kind}` }))
  // the size as well as the hash: a copy kept under the right hash but the wrong length is
  // what a device that was activated on the day it first synced was left with (see getSeries)
  const todo = wanted.filter(s => { const have = stored.get(s.key); return !have || have.hash !== s.hash || have.size !== s.size })
  const wantedKeys = new Set(wanted.map(s => s.key))
  const stale = [...stored.keys()].filter(k => !wantedKeys.has(k))
  if (stale.length) await db.series.bulkDelete(stale)

  let done = 0
  let failed = 0
  onProgress({ state: 'running', done, total: todo.length })
  const queue = [...todo]
  let freed = false
  const worker = async () => {
    for (;;) {
      const s = queue.shift()
      if (!s) return
      try {
        const bytes = await getSeries(s.year, s.station, s.kind, s)
        await db.series.put({ key: s.key, station: s.station, year: s.year, kind: s.kind, hash: s.hash, bytes })
      } catch (e) {
        // Out of room: the stream plates go first and this series is tried again. They are
        // a convenience the app can fetch back; the predictions are the app itself.
        if (!freed && e instanceof Error && (e.name === 'QuotaExceededError' || /quota/i.test(e.message))) {
          freed = true
          await freePlates()
          queue.unshift(s)
          continue
        }
        failed++
      }
      done++
      onProgress({ state: 'running', done, total: todo.length })
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, worker))
  if (failed === 0) {
    await kvSet('lastSync', new Date().toISOString())
    onProgress({ state: 'done', done, total: todo.length })
  } else {
    onProgress({ state: 'failed', done, total: todo.length, message: `${failed} downloads failed` })
  }
  if (navigator.storage?.persist) void navigator.storage.persist()
}
